import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { EMPTY_VALUE, formatNumber, formatPercent } from "@/i18n/format";
import type {
  MatchingPlaygroundResponse,
  MatchingPlaygroundResult,
  MatchingSettings,
} from "@/services/moderators/generated/model";
import {
  getModeratorsMatchingSettingsRetrieveQueryKey,
  useModeratorsMatchingPlaygroundCreate,
  useModeratorsMatchingSettingsRetrieve,
  useModeratorsMatchingSettingsUpdatePartialUpdate,
} from "@/services/moderators/generated/moderators-matching/moderators-matching";
import "./ModeratorPage.css";
import "./ModeratorMatchingPage.css";

type ItemType = "product" | "service";

const PCT = (v: number | undefined | null): string => (v == null ? EMPTY_VALUE : formatPercent(v * 100));

const SCORE = (v: number | undefined | null): string =>
  v == null ? EMPTY_VALUE : formatNumber(v, { minimumFractionDigits: 3, maximumFractionDigits: 3 });

// Matching settings / playground are super-moderator only (the backend
// enforces `IsSuperModerator`). Render the content — and therefore fire
// its queries — only for super moderators; everyone else gets a notice.
const ModeratorMatchingPage = () => (
  <ModeratorLayout>
    {(moderator) => (moderator.role === "SUPERMODERATOR" ? <MatchingContent /> : <MatchingRestricted />)}
  </ModeratorLayout>
);

const MatchingRestricted = () => {
  const { t } = useTranslation("deals");
  return (
    <>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("matching.title")}</h1>
          <p className="mod-page__subtitle">{t("shared.superOnly")}</p>
        </div>
      </header>
      <div className="mod-page__empty">{t("matching.restricted")}</div>
    </>
  );
};

const MatchingContent = () => {
  const { t } = useTranslation(["deals", "common"]);
  const queryClient = useQueryClient();

  // Settings load + update
  const { data: settings, isLoading: settingsLoading } = useModeratorsMatchingSettingsRetrieve();
  const updateMutation = useModeratorsMatchingSettingsUpdatePartialUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getModeratorsMatchingSettingsRetrieveQueryKey(),
        });
      },
    },
  });

  // Local form state — initialized from server settings
  const [form, setForm] = useState<Partial<MatchingSettings>>({});
  const effective = { ...(settings as MatchingSettings | undefined), ...form };

  const onSettingChange = <K extends keyof MatchingSettings>(key: K, value: MatchingSettings[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveSettings = () => {
    if (!Object.keys(form).length) return;
    updateMutation.mutate({ data: form });
    setForm({});
  };

  // Playground state
  const [pgQuery, setPgQuery] = useState("");
  const [pgType, setPgType] = useState<ItemType>("product");
  const [pgRegion, setPgRegion] = useState("");
  const [pgQuantity, setPgQuantity] = useState("");
  const [pgPrice, setPgPrice] = useState("");
  const [pgDeliveryTime, setPgDeliveryTime] = useState("");
  const [pgOverrides, setPgOverrides] = useState<{
    cosine_weight?: number;
    quantity_weight?: number;
    price_weight?: number;
    delivery_time_weight?: number;
    top_k?: number;
  }>({});

  const playgroundMutation = useModeratorsMatchingPlaygroundCreate();
  const playgroundResponse = playgroundMutation.data as MatchingPlaygroundResponse | undefined;

  const runPlayground = () => {
    if (!pgQuery.trim()) return;
    const data: Record<string, unknown> = {
      description: pgQuery.trim(),
      type: pgType,
    };
    if (pgRegion.trim()) data.region = pgRegion.trim();
    if (pgQuantity.trim()) data.quantity = Number(pgQuantity);
    if (pgPrice.trim()) data.price = Number(pgPrice);
    if (pgDeliveryTime.trim()) data.delivery_time = pgDeliveryTime.trim();
    Object.assign(data, pgOverrides);
    // biome-ignore lint/suspicious/noExplicitAny: orval-generated request type
    playgroundMutation.mutate({ data: data as any });
  };

  const setOverride = (key: string, raw: string) => {
    setPgOverrides((prev) => {
      if (!raw.trim()) {
        const { [key]: _, ...rest } = prev as Record<string, number>;
        return rest;
      }
      const n = Number(raw);
      if (Number.isNaN(n)) return prev;
      return { ...prev, [key]: n };
    });
  };

  return (
    <div className="moderator-page moderator-matching">
      <header className="moderator-page__header">
        <div>
          <h1 className="moderator-page__title">{t("matching.title")}</h1>
          <p className="moderator-page__subtitle">{t("matching.subtitle")}</p>
        </div>
        <Link to="/moderator/matching/runs" className="mod-page__btn mod-page__btn--ghost">
          <i className="fa-solid fa-list-check" /> <span>{t("matching.pipelineRuns")}</span>
        </Link>
      </header>

      <section className="moderator-matching__panel">
        <header className="moderator-matching__panel-header">
          <h2>{t("matching.settings.title")}</h2>
          <p className="moderator-matching__hint">{t("matching.settings.hint")}</p>
        </header>

        {settingsLoading ? (
          <div className="moderator-matching__loading">
            <i className="fa-solid fa-spinner fa-spin" /> {t("matching.settings.loading")}
          </div>
        ) : settings ? (
          <div className="moderator-matching__settings-grid">
            <SettingRow
              label={t("matching.settings.cosine")}
              hint={t("matching.settings.cosineHint")}
              value={effective.cosineWeight}
              step={0.05}
              onChange={(v) => onSettingChange("cosineWeight", v)}
            />
            <SettingRow
              label={t("matching.settings.quantity")}
              hint={t("matching.settings.structuredHint")}
              value={effective.quantityWeight}
              step={0.05}
              onChange={(v) => onSettingChange("quantityWeight", v)}
            />
            <SettingRow
              label={t("matching.settings.price")}
              hint={t("matching.settings.structuredHint")}
              value={effective.priceWeight}
              step={0.05}
              onChange={(v) => onSettingChange("priceWeight", v)}
            />
            <SettingRow
              label={t("matching.settings.deliveryTime")}
              hint={t("matching.settings.structuredHint")}
              value={effective.deliveryTimeWeight}
              step={0.05}
              onChange={(v) => onSettingChange("deliveryTimeWeight", v)}
            />
            <SettingRow
              label={t("matching.settings.topK")}
              hint={t("matching.settings.topKHint")}
              value={effective.topK}
              min={1}
              max={100}
              step={1}
              isInt
              onChange={(v) => onSettingChange("topK", v)}
            />
            <SettingRow
              label={t("matching.settings.minScore")}
              hint={t("matching.settings.minScoreHint")}
              value={effective.minScoreThreshold}
              min={0}
              max={100}
              step={1}
              isInt
              onChange={(v) => onSettingChange("minScoreThreshold", v)}
            />
          </div>
        ) : (
          <p className="moderator-matching__empty">{t("matching.settings.empty")}</p>
        )}

        <footer className="moderator-matching__panel-footer">
          <button
            type="button"
            className="moderator-page__button moderator-page__button--primary"
            onClick={saveSettings}
            disabled={updateMutation.isPending || !Object.keys(form).length}
          >
            {updateMutation.isPending ? t("common:actions.saving") : t("common:actions.saveChanges")}
          </button>
          {Object.keys(form).length > 0 && (
            <button
              type="button"
              className="moderator-page__button moderator-page__button--ghost"
              onClick={() => setForm({})}
            >
              {t("matching.settings.discard")}
            </button>
          )}
        </footer>
      </section>

      <section className="moderator-matching__panel">
        <header className="moderator-matching__panel-header">
          <h2>{t("matching.playground.title")}</h2>
          <p className="moderator-matching__hint">{t("matching.playground.hint")}</p>
        </header>

        <div className="moderator-matching__playground-form">
          <div className="moderator-matching__field moderator-matching__field--wide">
            <label htmlFor="pg-query">{t("matching.playground.query")}</label>
            <textarea
              id="pg-query"
              rows={2}
              placeholder={t("matching.playground.queryPlaceholder")}
              value={pgQuery}
              onChange={(e) => setPgQuery(e.target.value)}
            />
          </div>
          <div className="moderator-matching__field">
            <label htmlFor="pg-type">{t("matching.playground.type")}</label>
            <select id="pg-type" value={pgType} onChange={(e) => setPgType(e.target.value as ItemType)}>
              <option value="product">{t("matching.playground.product")}</option>
              <option value="service">{t("matching.playground.service")}</option>
            </select>
          </div>
          <div className="moderator-matching__field">
            <label htmlFor="pg-region">{t("matching.playground.region")}</label>
            <input
              id="pg-region"
              type="text"
              value={pgRegion}
              onChange={(e) => setPgRegion(e.target.value)}
              placeholder={t("matching.playground.regionPlaceholder")}
            />
          </div>
          <div className="moderator-matching__field">
            <label htmlFor="pg-quantity">{t("matching.playground.quantity")}</label>
            <input
              id="pg-quantity"
              type="number"
              min={0}
              value={pgQuantity}
              onChange={(e) => setPgQuantity(e.target.value)}
            />
          </div>
          <div className="moderator-matching__field">
            <label htmlFor="pg-price">{t("matching.playground.price")}</label>
            <input
              id="pg-price"
              type="number"
              min={0}
              step={0.01}
              value={pgPrice}
              onChange={(e) => setPgPrice(e.target.value)}
            />
          </div>
          <div className="moderator-matching__field">
            <label htmlFor="pg-delivery">{t("matching.playground.deliveryTime")}</label>
            <input
              id="pg-delivery"
              type="text"
              value={pgDeliveryTime}
              onChange={(e) => setPgDeliveryTime(e.target.value)}
              placeholder={t("matching.playground.deliveryPlaceholder")}
            />
          </div>

          <div className="moderator-matching__field moderator-matching__field--wide">
            <label>{t("matching.playground.overrides")}</label>
            <div className="moderator-matching__overrides-grid">
              <OverrideInput
                label={t("matching.playground.override.cosine")}
                value={pgOverrides.cosine_weight}
                onChange={(v) => setOverride("cosine_weight", v)}
                step={0.05}
                min={0}
                max={1}
              />
              <OverrideInput
                label={t("matching.playground.override.quantity")}
                value={pgOverrides.quantity_weight}
                onChange={(v) => setOverride("quantity_weight", v)}
                step={0.05}
                min={0}
                max={1}
              />
              <OverrideInput
                label={t("matching.playground.override.price")}
                value={pgOverrides.price_weight}
                onChange={(v) => setOverride("price_weight", v)}
                step={0.05}
                min={0}
                max={1}
              />
              <OverrideInput
                label={t("matching.playground.override.delivery")}
                value={pgOverrides.delivery_time_weight}
                onChange={(v) => setOverride("delivery_time_weight", v)}
                step={0.05}
                min={0}
                max={1}
              />
              <OverrideInput
                label={t("matching.playground.override.topK")}
                value={pgOverrides.top_k}
                onChange={(v) => setOverride("top_k", v)}
                step={1}
                min={1}
                max={100}
                isInt
              />
            </div>
          </div>
        </div>

        <footer className="moderator-matching__panel-footer">
          <button
            type="button"
            className="moderator-page__button moderator-page__button--primary"
            onClick={runPlayground}
            disabled={playgroundMutation.isPending || !pgQuery.trim()}
          >
            {playgroundMutation.isPending ? t("matching.playground.searching") : t("matching.playground.run")}
          </button>
          {playgroundResponse?.totalResults != null && (
            <span className="moderator-matching__count">
              {t("matching.playground.candidates", { count: playgroundResponse.totalResults })}
            </span>
          )}
        </footer>

        {playgroundMutation.isError && (
          <div className="moderator-matching__error" role="alert">
            <i className="fa-solid fa-circle-exclamation" /> {t("matching.playground.error")}
          </div>
        )}

        {playgroundResponse && playgroundResponse.results.length > 0 && (
          <div className="moderator-matching__results">
            <table className="moderator-matching__results-table">
              <thead>
                <tr>
                  <th>{t("matching.playground.table.rank")}</th>
                  <th>{t("matching.playground.table.supplier")}</th>
                  <th>{t("matching.playground.table.product")}</th>
                  <th>{t("matching.playground.table.final")}</th>
                  <th>{t("matching.playground.table.cosine")}</th>
                  <th>{t("matching.playground.table.structured")}</th>
                  <th>{t("matching.playground.table.region")}</th>
                </tr>
              </thead>
              <tbody>
                {playgroundResponse.results.map((r: MatchingPlaygroundResult, i: number) => (
                  <tr key={r.productId}>
                    <td>#{i + 1}</td>
                    <td>{r.companyName || "—"}</td>
                    <td>
                      <div className="moderator-matching__cell-name">{r.name}</div>
                      <div className="moderator-matching__cell-sub">{r.productId}</div>
                    </td>
                    <td>
                      <strong>{PCT(r.finalScore)}</strong>
                    </td>
                    <td>{SCORE(r.similarityScore)}</td>
                    <td>{SCORE(r.additionalScore)}</td>
                    <td>{r.region || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {playgroundResponse && playgroundResponse.results.length === 0 && (
          <p className="moderator-matching__empty">{t("matching.playground.noMatches")}</p>
        )}
      </section>
    </div>
  );
};

interface SettingRowProps {
  label: string;
  hint?: string;
  value: number | undefined;
  min?: number;
  max?: number;
  step?: number;
  isInt?: boolean;
  onChange: (v: number) => void;
}

const SettingRow = ({
  label,
  hint,
  value,
  min = 0,
  max = 1,
  step = 0.05,
  isInt = false,
  onChange,
}: SettingRowProps) => (
  <div className="moderator-matching__setting">
    <div className="moderator-matching__setting-head">
      <label>{label}</label>
      <span className="moderator-matching__setting-value">
        {value == null
          ? EMPTY_VALUE
          : isInt
            ? formatNumber(value)
            : formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
    {hint && <p className="moderator-matching__setting-hint">{hint}</p>}
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value ?? min}
      onChange={(e) => onChange(isInt ? parseInt(e.target.value, 10) : Number(e.target.value))}
    />
  </div>
);

interface OverrideInputProps {
  label: string;
  value: number | undefined;
  onChange: (raw: string) => void;
  step: number;
  min: number;
  max: number;
  isInt?: boolean;
}

const OverrideInput = ({ label, value, onChange, step, min, max, isInt = false }: OverrideInputProps) => (
  <label className="moderator-matching__override">
    <span>{label}</span>
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value ?? ""}
      placeholder={isInt ? "" : "0.00"}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

export default ModeratorMatchingPage;
